import { ModelPreview } from '@/components/project-detail/ModelPreview';
import { CollisionStatusTag } from '@/components/projects/CollisionStatusTag';
import type { ProjectListItem } from '@/types/project.types';
import { extractModelIdFromUrl, formatDateTime, formatUserName } from '@/utils/formatters';
import { AlertTriangle, Calendar, User } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';

interface ProjectCardProps {
  project: ProjectListItem;
  onClick: (projectId: string) => void;
  onUserClick?: (ownerId: string) => void;
  isSelected?: boolean;
  onSelect?: (projectId: string, selected: boolean) => void;
}

export const ProjectCard: FC<ProjectCardProps> = ({ project, onClick, onUserClick, isSelected = false, onSelect }) => {
  // Prefer target, fallback to blank
  const previewUrl = project.target_png_url || project.blank_png_url;
  const modelId = extractModelIdFromUrl(previewUrl);
  const userName = formatUserName(project);

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering project click
    if (onUserClick) {
      onUserClick(project.owner_id);
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering project click
    if (onSelect) {
      onSelect(project.id, !isSelected);
    }
  };

  const hintClass = `project-apt-error-hint-${project.id}`;

  return (
    <div
      className={`bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer relative ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      onClick={() => onClick(project.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(project.id);
        }
      }}
      tabIndex={0}
      role="button"
    >
      {/* Error / warning badge */}
      {project.has_errors && (
        <>
          <Tooltip target={`.${hintClass}`} position="bottom" style={{ maxWidth: '24rem' }} />
          <div
            className={`${hintClass} absolute top-2 left-2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-600 cursor-help`}
            data-pr-tooltip={project.apt_generation_error ?? 'Project contains APT generation errors'}
            aria-label={project.apt_generation_error ?? 'Project contains APT generation errors'}
          >
            <AlertTriangle size={16} />
          </div>
        </>
      )}
      {/* Checkbox in top right corner */}
      {onSelect && (
        <div
          className="absolute top-2 right-2 z-10"
          onClick={handleSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              if (onSelect) {
                onSelect(project.id, !isSelected);
              }
            }
          }}
          tabIndex={0}
          role="button"
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelect) {
                onSelect(project.id, e.target.checked);
              }
            }}
            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {/* Preview container */}
      <div className="aspect-video bg-gray-100 rounded mb-4 overflow-hidden">
        <ModelPreview modelId={modelId} alt={`Preview of ${project.title}`} />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.title}</h3>

      {/* Part Type */}
      {project.part_type && (
        <div className="mb-2">
          <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded" title="Typ součásti">
            {project.part_type}
          </span>
        </div>
      )}

      {/* Metadata */}
      <div className="flex flex-col gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar size={14} />
          <span>{formatDateTime(project.created_at)}</span>
        </div>
        <div
          className={`flex items-center gap-2 text-sm text-gray-500 ${onUserClick ? 'cursor-pointer hover:text-gray-700' : ''}`}
          onClick={handleUserClick}
          onKeyDown={(e) => {
            if (onUserClick && (e.key === 'Enter' || e.key === ' ')) {
              e.stopPropagation();
              onUserClick(project.owner_id);
            }
          }}
          tabIndex={onUserClick ? 0 : undefined}
          role={onUserClick ? 'button' : undefined}
        >
          <User size={14} />
          <span className="truncate" title={userName}>
            {userName}
          </span>
        </div>
      </div>

      {/* Badges for Target/Blank (left) + collision status (bottom-right) */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {project.target_png_url && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">Target</span>
          )}
          {project.blank_png_url && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Blank</span>
          )}
        </div>
        <CollisionStatusTag state={project.collision_simulation_state} />
      </div>
    </div>
  );
};
